import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { TagCombobox } from '@/components/TagCombobox'
import SuggestCombobox from '@/components/admin/research/SuggestCombobox'
import type { ResearchCustomFieldDef } from '@/types/research'

type Props = {
  fields: ResearchCustomFieldDef[]
  value: Record<string, string | string[]>
  onChange: (key: string, value: string | string[]) => void
}

export default function CustomFieldsSection({ fields, value, onChange }: Props) {
  if (fields.length === 0) return null

  return (
    <Card>
      <CardHeader>
        <CardTitle>Custom fields</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4">
          {fields.map(field => {
            const raw = value[field.key]

            if (field.field_type === 'tag') {
              return (
                <div key={field.id} className="col-span-2">
                  <TagCombobox
                    label={field.label}
                    suggestions={field.options}
                    selected={Array.isArray(raw) ? raw : []}
                    onChange={items => onChange(field.key, items)}
                    allowCustom
                  />
                  {field.help_text && (
                    <p className="mt-1 text-xs text-muted-foreground">{field.help_text}</p>
                  )}
                </div>
              )
            }

            const stringValue = typeof raw === 'string' ? raw : ''

            if (field.field_type === 'select') {
              return (
                <SuggestCombobox
                  key={field.id}
                  label={field.label}
                  id={`custom-${field.key}`}
                  suggestions={field.options}
                  value={stringValue}
                  onChange={v => onChange(field.key, v)}
                  required={field.required}
                  helpText={field.help_text ?? undefined}
                />
              )
            }

            const inputType =
              field.field_type === 'number' ? 'number' : field.field_type === 'url' ? 'url' : 'text'

            return (
              <div key={field.id} className="space-y-2">
                <Label htmlFor={`custom-${field.key}`}>
                  {field.label}
                  {field.required && <span className="text-destructive"> *</span>}
                </Label>
                <Input
                  id={`custom-${field.key}`}
                  type={inputType}
                  value={stringValue}
                  onChange={e => onChange(field.key, e.target.value)}
                />
                {field.help_text && (
                  <p className="text-xs text-muted-foreground">{field.help_text}</p>
                )}
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
